import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  Injector,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  runInInjectionContext,
  signal,
  Signal,
  ViewChild,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {MatBadgeModule} from '@angular/material/badge';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckbox, MatCheckboxModule} from '@angular/material/checkbox';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatTree, MatTreeModule, MatTreeNestedDataSource} from '@angular/material/tree';
import {Category} from '@models/category';
import {FieldTypeConfig, FormlyAttributes} from '@ngx-formly/core';
import {FieldType} from '@ngx-formly/material';
import {Observable, Subscription} from 'rxjs'; // Added RxJS imports

type CatsById = {
  [key: string]: Category;
};
type SelectionByCatId = {
  [key: string]: boolean;
};
type IndeterminateByCatId = {
  [key: string]: boolean;
};

@Component({
  standalone: true,
  selector: 'app-categories-select-tree',
  templateUrl: './categories-select-tree.component.html',
  styleUrl: './categories-select-tree.component.scss',
  imports: [
    MatBadgeModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatTreeModule,
    ReactiveFormsModule,
    FormlyAttributes,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoriesSelectTreeComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {
  // =========================================================================================
  // Public properties
  // =========================================================================================

  /** The data source for the Angular Material Tree. */
  dataSource = new MatTreeNestedDataSource<Category>();

  /** Signal indicating if the category data has been loaded and processed. */
  dataLoaded: WritableSignal<boolean> = signal(false);

  /** Signal holding the selection state (checked: true/false) (all categories by ID). */
  checklistSelection: WritableSignal<SelectionByCatId> = signal({});

  /** Signal holding the indeterminate state for ancestor nodes. */
  indeterminateSelection: WritableSignal<IndeterminateByCatId> = signal({});

  /** MatTree accessor for expansion logic in the template. */
  expansionKey = (node: Category) => node.id;

  /** MatTree accessors */
  childrenAccessor = (node: Category) => node.children ?? [];

  hasChild = (_: number, node: Category) => {
    return !!node.children && node.children.length > 0;
  };

  // =========================================================================================
  // ViewChildren / View accessors
  // =========================================================================================

  /** Accessor for all rendered MatCheckbox elements. */
  @ViewChildren(MatCheckbox) checkboxes: QueryList<MatCheckbox>;

  /** Private reference to the MatTree component. */
  private matCatTree: MatTree<Category, string>;

  /** Public setter/getter for the MatTree instance, triggering view-ready logic on change. */
  @ViewChild(MatTree) set tree(value: MatTree<Category, string>) {
    this.matCatTree = value;
    this.handleViewReady();
  }
  get tree(): MatTree<Category, string> {
    return this.matCatTree;
  }

  /** Flag to prevent re-running initial selection logic on subsequent change detection cycles. */
  private initialValueHandled: boolean = false;

  /** Flat map of all categories by ID for quick lookups. */
  private catsById: CatsById = {};

  /** Cache of the raw category data array. */
  private loadedCats: Category[] = [];

  /** Subscription for the options Observable. */
  private optionsSubscription: Subscription | null = null;

  // =========================================================================================
  // 4. CONSTRUCTOR & LIFECYCLE HOOKS
  // =========================================================================================

  constructor(
    private readonly renderer2: Renderer2,
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector,
  ) {
    super();

    runInInjectionContext(this.injector, () => {
      // Data arrival and component re-initialization.
      effect(() => {
        const dataSignal = this.props['categoryDataSignal'] as Signal<Category[] | undefined>;

        if (typeof dataSignal === 'function') {
          const cats = dataSignal();
          this.processCategoryData(cats);
        }
      });
    });
  }

  ngOnInit() {
    // Logic to handle the 'options' Observable (standard Formly practice)
    const options = this.props['options'];
    if (options instanceof Observable) {
      this.optionsSubscription = options.subscribe((cats: Category[] | undefined) => {
        this.processCategoryData(cats);
      });
    }

    this.field.formControl.valueChanges.subscribe(catIds => {
      // Handle model updates that occur after the initial component load/render.
      if (this.dataLoaded() && this.initialValueHandled) {
        queueMicrotask(() => {
          this.updateChecklistSelection(catIds ?? []);
          this.cdr.markForCheck();
        });
      }
    });
  }

  /** Cleanup subscription to prevent memory leaks. */
  override ngOnDestroy() {
    this.optionsSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  // =========================================================================================
  // Private data processing helper
  // =========================================================================================

  private processCategoryData(cats: Category[] | undefined) {
    const hasCats = cats && cats.length > 0;
    const newDataArrived = hasCats && this.loadedCats !== cats;
    // Trigger re-initialization if category data is present AND component was already initialized
    const reInitForReuse = hasCats && this.initialValueHandled;

    if (newDataArrived || reInitForReuse) {
      this.loadedCats = cats as Category[];
      this.indexAllCategories(cats as Category[]);

      this.updateMatTreeData(cats as Category[]);
      this.dataLoaded.set(true);

      // Reset flags to force re-run of selection logic (via the 'set tree' method)
      this.initialValueHandled = false;
      this.checklistSelection.set({});
      this.indeterminateSelection.set({});

      this.cdr.markForCheck();
    }
  }

  // =========================================================================================
  // PUBLIC INTERACTION METHODS
  // =========================================================================================

  /** Handles the click/change event on any MatCheckbox. */
  handleToggle(cat: Category) {
    const selectionByCatId = this.checklistSelection();
    const newState = !selectionByCatId[cat.id];

    this.setDescendantSelection(cat, newState, selectionByCatId);
    this.updateAncestorState(cat, selectionByCatId);

    this.checklistSelection.set(selectionByCatId);

    // Update Formly model value
    this.value = this.buildFormlyValue(this.dataSource.data, selectionByCatId);

    this.formControl.markAsDirty();
    this.formControl.markAsTouched();

    this.cdr.markForCheck();

    this.syncCheckboxUI(selectionByCatId);
  }

  /** Ensures `name` attribute is present on dynamically rendered checkboxes for forms/accessibility. */
  handleExpand() {
    if (!this.checkboxes) return;

    for (const c of this.checkboxes.toArray()) {
      if (c._inputElement) {
        this.renderer2.setAttribute(c._inputElement.nativeElement, 'name', this.field.key.toString());
      }
    }
  }

  // =========================================================================================
  // 6. PRIVATE LOGIC & HELPER METHODS
  // =========================================================================================

  /**
   * Logic to run once the MatTree view is initialized or available.
   */
  private handleViewReady(): void {
    if (this.matCatTree) {
      this.cdr.markForCheck();

      // 1. Ensure data binding if data arrived before the MatTree view was ready.
      if (this.loadedCats?.length > 0 && !this.dataLoaded()) {
        this.updateMatTreeData(this.loadedCats);
      }

      // 2. Initial selection/Re-initialization logic
      if (this.dataLoaded() && !this.initialValueHandled) {
        // Use queueMicrotask to ensure we wait for Angular to finish rendering the tree elements
        // (including the checkboxes) before we try to select them.
        queueMicrotask(() => {
          const initialValue = this.formControl.value ?? [];

          // Safety check: ensure the checkboxes are available after the view renders
          if (this.checkboxes.length > 0) {
            this.updateChecklistSelection(initialValue);
            this.initialValueHandled = true;
            this.cdr.markForCheck();
          }
        });
      }
    }
  }

  private updateMatTreeData(data: Category[]): void {
    if (this.dataSource.data === data || data.length === 0) {
      return;
    }

    this.dataSource.data = data;

    if (this.tree) {
      this.tree.dataSource = this.dataSource;
    }
  }

  /** Populates the internal selection maps based on the list of selected category IDs. */
  private updateChecklistSelection(catIds: string[]) {
    const selectionByCatId: SelectionByCatId = {};
    const indeterminateByCatId: IndeterminateByCatId = {};

    if (Object.keys(this.catsById).length === 0) {
      console.warn('[CATEGORY SELECT TREE] Index not ready, skipping selection update.');
      return;
    }

    // Initialize all as unselected
    for (const k of Object.keys(this.catsById)) {
      selectionByCatId[k] = false;
      indeterminateByCatId[k] = false;
    }

    // Set descendant selections first (assuming input IDs are minimal/tertiary)
    for (const catId of catIds) {
      const node = this.catsById[catId];
      if (node) {
        this.setDescendantSelection(node, true, selectionByCatId);
      }
    }

    // Calculate indeterminate state for ancestors
    for (const rootNode of this.dataSource.data) {
      this.calculateInitialIndeterminateState(selectionByCatId, indeterminateByCatId, [rootNode]);
    }

    this.checklistSelection.set(selectionByCatId);
    this.indeterminateSelection.set(indeterminateByCatId);

    // Sync the actual UI checkboxes
    if (this.checkboxes.length > 0) {
      this.syncCheckboxUI(this.checklistSelection());
    }
  }

  /** Manually sets the checkbox state (checked/indeterminate) on the underlying DOM elements. */
  private syncCheckboxUI(selection: SelectionByCatId) {
    const checkboxesArray = this.checkboxes?.toArray() ?? [];
    if (checkboxesArray.length === 0) return;

    for (const c of checkboxesArray) {
      const catId = c.id.replace('-mat-checkbox', '');
      const isSelected = selection[catId];
      const isIndeterminate = this.indeterminateSelection()[catId];

      if (c._inputElement) {
        c.writeValue(isSelected);
        this.renderer2.setAttribute(c._inputElement.nativeElement, 'checked', `${isSelected}`);
        this.renderer2.setProperty(c._inputElement.nativeElement, 'indeterminate', isIndeterminate);
      }
    }
  }

  /** Builds a flat map of all categories by ID for quick lookup. */
  private indexAllCategories(cats: Category[]) {
    const _index = (nodes: Category[]) => {
      for (const node of nodes) {
        this.catsById[node.id] = node;
        if (node.children) {
          _index(node.children);
        }
      }
    };
    this.catsById = {}; // Clear previous index
    _index(cats);
  }

  /** Recursively calculates the initial indeterminate and selected state during component load. */
  private calculateInitialIndeterminateState(
    selection: SelectionByCatId,
    indeterminate: IndeterminateByCatId,
    nodes: Category[],
  ) {
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        this.calculateInitialIndeterminateState(selection, indeterminate, node.children);

        const anyChildSelectedOrIndeterminate = node.children.some(
          child => selection[child.id] || indeterminate[child.id],
        );

        const allChildrenFullySelected = node.children.every(child => selection[child.id] && !indeterminate[child.id]);

        if (allChildrenFullySelected) {
          indeterminate[node.id] = false;
          selection[node.id] = true;
        } else if (anyChildSelectedOrIndeterminate) {
          selection[node.id] = false;
          indeterminate[node.id] = true;
        } else {
          indeterminate[node.id] = false;
          selection[node.id] = false;
        }
      } else {
        indeterminate[node.id] = false;
      }
    }
  }

  /** Recursively sets the selection state for a node and all of its descendants. */
  private setDescendantSelection(node: Category, isSelected: boolean, selection: SelectionByCatId) {
    selection[node.id] = isSelected;

    const indeterminate = this.indeterminateSelection();
    indeterminate[node.id] = false;

    if (node.children) {
      for (const child of node.children) {
        this.setDescendantSelection(this.catsById[child.id], isSelected, selection);
      }
    }
    this.indeterminateSelection.set(indeterminate);
  }

  /** Walks up the tree to update parent selection/indeterminate state based on children. */
  private updateAncestorState(node: Category, selection: SelectionByCatId) {
    const indeterminate = this.indeterminateSelection();

    let current = node;
    while (current.parent_id) {
      const parent = this.catsById[current.parent_id];
      if (!parent?.children) break;

      const anyChildSelectedOrIndeterminate = parent.children.some(
        child => selection[child.id] || indeterminate[child.id],
      );

      const allChildrenFullySelected = parent.children.every(child => selection[child.id] && !indeterminate[child.id]);

      indeterminate[parent.id] = false;

      if (allChildrenFullySelected) {
        selection[parent.id] = true;
      } else if (anyChildSelectedOrIndeterminate) {
        selection[parent.id] = false;
        indeterminate[parent.id] = true;
      } else {
        selection[parent.id] = false;
      }

      current = parent;
    }
    this.indeterminateSelection.set(indeterminate);
  }

  // --- Formly Output Value Builders ---

  /** Determines the final output array based on the configured outputMode. */
  private buildFormlyValue(nodes: Category[], selection: SelectionByCatId): string[] {
    const outputMode = this.props['outputMode'] as 'minimal' | 'tertiaryOnly' | undefined;

    if (outputMode === 'tertiaryOnly') {
      return this.getTertiaryOnlyValue(nodes, selection);
    } else {
      return this.getMinimalValue(nodes, selection);
    }
  }

  /** Output Mode: Returns the highest-level selected categories (minimal representation). */
  private getMinimalValue(nodes: Category[], selection: SelectionByCatId): string[] {
    const result: string[] = [];

    for (const node of nodes) {
      if (selection[node.id]) {
        result.push(node.id);
        // Stop descending if the ancestor is selected
        continue;
      }

      if (node.children && node.children.length > 0) {
        result.push(...this.getMinimalValue(node.children, selection));
      }
    }

    return result;
  }

  /** Output Mode: Returns only selected categories that have no children (tertiary/leaf nodes). */
  private getTertiaryOnlyValue(nodes: Category[], selection: SelectionByCatId): string[] {
    const result: string[] = [];

    for (const node of nodes) {
      const isLeaf = !node.children || node.children.length === 0;

      if (isLeaf && selection[node.id]) {
        result.push(node.id);
      }

      if (node.children && node.children.length > 0) {
        result.push(...this.getTertiaryOnlyValue(node.children, selection));
      }
    }

    return result;
  }
}
